import React, { useState, useCallback, useEffect, useMemo } from 'react';

import {
    TablePagination,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    Paper,
} from '@mui/material';
import { sanitizeGradeShortCode } from '@/src/utils';
import { useAuth } from '@/src/app/context/useAuth';
import './css/DataTable.css';

export interface TableColumn {
    dataKey: string;
    header: string;
    allowSort?: boolean;
    sortMethod?: (a: any, b: any, sortOrder: 'asc' | 'desc') => number;
    formatter?: (row: any, index?: number) => React.ReactNode | string;
    align?: 'left' | 'right' | 'center' | 'inherit' | 'justify';
    hideColumn?: boolean;
    emptyValue?: string;
    headerSx?: object;
    cellSx?: object;
    excludeFromExport?: boolean;
}

interface DataTableProps {
    data: Array<any>;
    columns: TableColumn[];
    tableName: string;
    rowsPerPageList?: number[];
    rowsPerPageInitial?: number;
    defaultSortColumn?: string;
    defaultSortDirection?: 'asc' | 'desc';
    emptyMessage?: string;
    enableExportCSV?: boolean;
    alternatingRowColor?: boolean;
    rowSx?: (row: any, index: number) => object;
}

export default function DataTable(props: DataTableProps) {
    const {
        data,
        columns,
        tableName,
        rowsPerPageList = [10, 20, 50, 100],
        rowsPerPageInitial = 20,
        defaultSortColumn = '',
        defaultSortDirection = 'asc',
        emptyMessage = 'No data was found.',
        enableExportCSV = false,
        alternatingRowColor = false,
    } = props;

    // const PdfTableColors = {
    //     HeaderBackground: '#053E68',
    //     EvenRowBackground: '#ffffff',
    //     OddRowBackground: '#FFE066',
    //     LineColor: '#000000',
    //     HeaderText: '#fff',
    // } as const;

    const [page, setPage] = useState(0);
    const [rowsPerPage, setRowsPerPage] = useState(rowsPerPageInitial);
    const [activeSortColumn, setActiveSortColumn] = useState(defaultSortColumn);
    const [sortDirection, setSortDirection] = useState<'asc' | 'desc'>(defaultSortDirection);

    const { user } = useAuth();

    const grade = user?.alternateGradeColorShortCode
        ? sanitizeGradeShortCode(user?.alternateGradeColorShortCode)
        : sanitizeGradeShortCode(user?.gradeShortCode);

    const sortedData = useMemo(() => {
        if (!activeSortColumn) return data;
        const column = columns.find((col) => col.dataKey === activeSortColumn);

        if (column?.sortMethod) {
            return [...data].sort((a, b) =>
                column.sortMethod!(a[activeSortColumn], b[activeSortColumn], sortDirection)
            );
        } else {
            // Default fallback sort method (alphabetical)
            const collator = new Intl.Collator(undefined, { numeric: true, sensitivity: 'base' });
            const swap = sortDirection === 'asc' ? 1 : -1;

            return [...data].sort((a, b) => {
                return (
                    collator.compare(String(a[activeSortColumn]), String(b[activeSortColumn])) *
                    swap
                );
            });
        }
    }, [data, columns, activeSortColumn, sortDirection]);

    const paginatedData = sortedData.slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage);

    useEffect(() => {
        const maxPage = Math.max(0, Math.ceil(sortedData.length / rowsPerPage) - 1);
        if (page > maxPage) {
            setPage(maxPage);
        }
    }, [sortedData.length, rowsPerPage, page]);

    const onSortClick = useCallback(
        (allowSort: boolean, dataKey: string) => {
            if (!allowSort) {
                return null;
            } else {
                if (activeSortColumn === dataKey) {
                    setSortDirection(sortDirection === 'asc' ? 'desc' : 'asc');
                } else {
                    setActiveSortColumn(dataKey);
                    setSortDirection(defaultSortDirection);
                }
            }
        },
        [sortDirection, activeSortColumn, defaultSortDirection]
    );

    // function toDisplayString(value: any): string {
    //     const stripHtml = (html: string) =>
    //         html
    //             .replace(/<[^>]+>/g, ' ')
    //             .replace(/\s+/g, ' ')
    //             .trim();

    //     function extractStringFromChildren(children: any): string {
    //         if (typeof children === 'string' || typeof children === 'number')
    //             return String(children);
    //         if (Array.isArray(children)) {
    //             for (const child of children) {
    //                 const str = extractStringFromChildren(child);
    //                 if (str) return str;
    //             }
    //         }
    //         if (children && typeof children === 'object' && 'props' in children) {
    //             return extractStringFromChildren(children.props.children);
    //         }
    //         return '';
    //     }

    //     if (React.isValidElement(value)) {
    //         if (value.props && typeof value.props === 'object' && 'children' in value.props) {
    //             const extracted = extractStringFromChildren(value.props.children);
    //             if (extracted) return extracted;
    //         }
    //         try {
    //             const html = ReactDOMServer.renderToStaticMarkup(value);
    //             return stripHtml(html);
    //         } catch {
    //             return '';
    //         }
    //     }
    //     if (typeof value === 'object' && value !== null) {
    //         if ('children' in value) {
    //             const extracted = extractStringFromChildren((value as any).children);
    //             if (extracted) return extracted;
    //         }
    //         try {
    //             return JSON.stringify(value);
    //         } catch {
    //             return '';
    //         }
    //     }
    //     if (typeof value === 'undefined' || value === null) return '';
    //     return String(value);
    // }
    // const handleExportPDF = () => {
    //     pdfMake.vfs = pdfFonts.vfs;
    //     if (!sortedData.length) return;
    //     const exportColumns = columns.filter((col) => !col.hideColumn && !col.excludeFromExport);
    //     const headers = exportColumns.map((col) => toDisplayString(col.header));
    //     const keys = exportColumns.map((col) => col.dataKey);
    //     const body: any[][] = [];
    //     body.push(headers);
    //     sortedData.forEach((row, rowIndex) => {
    //         const dataRow = keys.map((key) => {
    //             let value = row[key as keyof typeof row];
    //             const colDef = columns.find((col) => col.dataKey === key);
    //             if (colDef?.formatter) {
    //                 value = colDef.formatter(row, rowIndex);
    //             }
    //             const strValue = toDisplayString(value).replace(/\n/g, ' ');
    //             return strValue;
    //         });
    //         body.push(dataRow);
    //     });

    //     const styledBody: any[][] = [];
    //     if (body.length > 0) {
    //         styledBody.push(body[0].map((cell: any) => ({ text: cell, style: 'tableHeader' })));

    //         for (let i = 1; i < body.length; i++) {
    //             styledBody.push(
    //                 body[i].map((cell: any) => ({ text: cell, style: 'tableCell', rowIndex: i }))
    //             );
    //         }
    //     }
    //     const docDefinition = {
    //         pageOrientation: 'landscape' as const,
    //         content: [
    //             { text: tableName, style: 'header' },
    //             {
    //                 table: {
    //                     headerRows: 1,
    //                     widths: Array(headers.length).fill('auto'),
    //                     body: styledBody,
    //                     dontBreakRows: true,
    //                 },
    //                 layout: {
    //                     fillColor: function (rowIndex: number) {
    //                         if (rowIndex === 0) return PdfTableColors.HeaderBackground;
    //                         return rowIndex % 2 === 0
    //                             ? PdfTableColors.EvenRowBackground
    //                             : PdfTableColors.OddRowBackground;
    //                     },
    //                     hLineColor: function () {
    //                         return PdfTableColors.LineColor;
    //                     },
    //                     vLineColor: function () {
    //                         return PdfTableColors.LineColor;
    //                     },
    //                     paddingTop: function () {
    //                         return 6;
    //                     },
    //                     paddingBottom: function () {
    //                         return 6;
    //                     },
    //                     paddingLeft: function () {
    //                         return 8;
    //                     },
    //                     paddingRight: function () {
    //                         return 8;
    //                     },
    //                 },
    //             },
    //         ],
    //         styles: {
    //             header: {
    //                 fontSize: 18,
    //                 bold: true,
    //                 color: PdfTableColors.HeaderBackground,
    //                 margin: [0, 0, 0, 10] as [number, number, number, number],
    //             },
    //             tableHeader: {
    //                 bold: true,
    //                 fontSize: 14,
    //                 color: PdfTableColors.HeaderText,
    //                 fillColor: PdfTableColors.HeaderBackground,
    //                 alignment: 'left' as any,
    //             },
    //             tableCell: {
    //                 fontSize: 12,
    //                 color: '#2C2C2C',
    //                 alignment: 'left' as any,
    //             },
    //         },
    //         defaultStyle: {
    //             fontSize: 12,
    //         },
    //     };
    //     pdfMake.createPdf(docDefinition).download(`${tableName}.pdf`);
    // };

    const handleExportCSV = () => {
        if (!sortedData.length) return;
        const exportColumns = columns.filter((col) => !col.hideColumn && !col.excludeFromExport);
        const headers = exportColumns.map((col) => col.header);
        const keys = exportColumns.map((col) => col.dataKey);
        const rows = sortedData
            .map((row, rowIndex) => {
                return keys.map((key) => {
                    let value = row[key as keyof typeof row];
                    const colDef = columns.find((col) => col.dataKey === key);
                    if (colDef?.formatter) {
                        const formatted = colDef.formatter(row, rowIndex);
                        if (typeof formatted === 'string' || typeof formatted === 'number') {
                            value = formatted;
                        }
                    }

                    if (typeof value === 'string') {
                        value = value.replace(/,/g, ' ').replace(/\n/g, ' ');
                    }
                    return value ?? '';
                });
            })
            .filter((row) => row !== null);
        let csv = '';
        csv += headers.join(',') + '\n';
        rows.forEach((row) => {
            csv += row.join(',') + '\n';
        });
        const blob = new Blob([csv], { type: 'text/csv' });
        const url = URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = `${tableName}.csv`;
        document.body.appendChild(a);
        a.click();
        document.body.removeChild(a);
        URL.revokeObjectURL(url);
    };

    if (!data.length) {
        return <h2>{emptyMessage}</h2>;
    }

    return (
        <div
            style={{
                borderRadius: '16px',
                overflow: 'visible',
                marginBottom: '12px',
                marginRight: '10px',
            }}
        >
            <TableContainer component={Paper}>
                <Table
                    sx={{
                        tableLayout: 'auto',
                        maxWidth: '100%',
                    }}
                    aria-label="simple table"
                >
                    <TableHead>
                        <TableRow>
                            {columns.map((col, index) => {
                                const {
                                    dataKey,
                                    header,
                                    align = 'right',
                                    allowSort = false,
                                    hideColumn,
                                    headerSx = {},
                                } = col;
                                if (hideColumn) return null;
                                return (
                                    <TableCell
                                        key={`${tableName}-header-${index}`}
                                        align={align}
                                        sx={{
                                            ...headerSx,
                                            position: 'sticky',
                                        }}
                                    >
                                        <div
                                            className={`flex flex-row items-center gap-x-1 ${allowSort ? 'cursor-pointer' : ''}`}
                                            style={{
                                                justifyContent:
                                                    align === 'center'
                                                        ? 'center'
                                                        : align === 'right'
                                                          ? 'flex-end'
                                                          : 'flex-start',
                                                minWidth: 0,
                                                flexWrap: 'nowrap',
                                            }}
                                            onClick={() => onSortClick(allowSort, dataKey)}
                                        >
                                            <span
                                                style={{
                                                    whiteSpace: 'nowrap',
                                                    overflow: 'hidden',
                                                    textOverflow: 'ellipsis',
                                                    maxWidth: '100%',
                                                }}
                                            >
                                                {header}
                                            </span>
                                            <span
                                                style={{
                                                    marginLeft: 4,
                                                    display: 'flex',
                                                    alignItems: 'center',
                                                }}
                                            >
                                                <SortIcon
                                                    allowSort={allowSort}
                                                    sortDirection={sortDirection}
                                                    isColumnActive={activeSortColumn === dataKey}
                                                />
                                            </span>
                                        </div>
                                    </TableCell>
                                );
                            })}
                        </TableRow>
                    </TableHead>
                    <TableBody>
                        {paginatedData.length > 0
                            ? paginatedData.map((row, index) => {
                                  return (
                                      <TableRow
                                          key={`${tableName}-row-${index}`}
                                          sx={{
                                              backgroundColor: alternatingRowColor
                                                  ? index % 2 === 0
                                                      ? '#ffffffff'
                                                      : `var(--pm-g${grade}-table)`
                                                  : 'transparent',
                                              '&:last-child td, &:last-child th': { border: 0 },
                                              ...(props.rowSx ? props.rowSx(row, index) : {}),
                                          }}
                                      >
                                          {columns.map((col, colIndex) => {
                                              const { formatter, dataKey, hideColumn, emptyValue } =
                                                  col;
                                              if (hideColumn) return null;

                                              let innerValue = formatter
                                                  ? formatter(row, index)
                                                  : row[dataKey];
                                              innerValue = innerValue || emptyValue;
                                              return (
                                                  <TableCell
                                                      key={`${tableName}-row-${index}-col-${colIndex}`}
                                                      align={col.align}
                                                      sx={{ ...col.cellSx }}
                                                  >
                                                      {innerValue}
                                                  </TableCell>
                                              );
                                          })}
                                      </TableRow>
                                  );
                              })
                            : null}
                    </TableBody>
                </Table>
                <div
                    className="flex justify-between items-center mt-2 px-2 table-footer"
                    style={{
                        minWidth: 'max-content',
                    }}
                >
                    {enableExportCSV && !!sortedData.length && (
                        <div className="flex justify-end mb-4 ml-2 mt-2 mr-4 gap-2">
                            <button
                                onClick={handleExportCSV}
                                className="bg-[var(--pm-accent)] text-black font-medium py-1 px-3 rounded-sm transition-colors duration-200 whitespace-nowrap"
                                style={{ fontSize: '14px' }}
                            >
                                Export CSV
                            </button>

                            {/* <button
                                onClick={handleExportPDF}
                                className="bg-[var(--pm-accent)] text-black font-medium py-1 px-3 rounded-sm transition-colors duration-200 whitespace-nowrap"
                                style={{ fontSize: '14px' }}
                            >
                                Export PDF
                            </button> */}
                        </div>
                    )}
                    <TablePagination
                        component="div"
                        count={data.length}
                        page={page}
                        showFirstButton
                        showLastButton
                        onPageChange={(_, newPage) => setPage(newPage)}
                        rowsPerPage={rowsPerPage}
                        onRowsPerPageChange={(event) => {
                            setRowsPerPage(parseInt(event.target.value));
                            setPage(0);
                        }}
                        rowsPerPageOptions={rowsPerPageList}
                        sx={{
                            width: '100%',
                            display: 'flex',
                            justifyContent: 'space-between',
                            flexWrap: 'wrap',
                            overflow: 'wrap',

                            '& .MuiTablePagination-toolbar': {
                                width: '100%',
                                padding: 0,
                            },
                            '& .MuiTablePagination-actions': {
                                marginLeft: 'auto',
                            },
                        }}
                    />
                </div>
            </TableContainer>
        </div>
    );
}

interface SortIconProps {
    allowSort: boolean;
    sortDirection: 'asc' | 'desc';
    isColumnActive: boolean;
}

function SortIcon({ allowSort, sortDirection, isColumnActive }: SortIconProps) {
    if (!allowSort) return null;

    const sortArrow = (
        <svg
            className={`h-6 w-6 fill-mpDBlue-5 ${sortDirection === 'asc' ? 'rotate-180' : 'rotate-0'} duration-200`}
        >
            <path d="M20 12l-1.41-1.41L13 16.17V4h-2v12.17l-5.58-5.59L4 12l8 8 8-8z" />
        </svg>
    );
    const filterIcon = (
        <svg className="flex h-6 w-6 fill-mpDBlue-5">
            <path d="M6 10.5a.5.5 0 0 1 .5-.5h3a.5.5 0 0 1 0 1h-3a.5.5 0 0 1-.5-.5zm-2-3a.5.5 0 0 1 .5-.5h7a.5.5 0 0 1 0 1h-7a.5.5 0 0 1-.5-.5zm-2-3a.5.5 0 0 1 .5-.5h11a.5.5 0 0 1 0 1h-11a.5.5 0 0 1-.5-.5z" />
        </svg>
    );

    if (isColumnActive) {
        return sortArrow;
    } else {
        return filterIcon;
    }
}
